/**
 * Validation and key-filtering utilities for editable table cells.
 *
 * Each helper returns true when the keystroke should be ALLOWED,
 * and calls `e.preventDefault()` when it should be blocked.
 */

import type { KeyboardEvent, ClipboardEvent } from 'react';

const NAVIGATION_KEYS = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab'];

const isModifierCombo = (e: KeyboardEvent<HTMLInputElement>): boolean =>
  (e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'v');

// ---- Number fields --------------------------------------------------------

const BLOCKED_NUMBER_SYMBOLS = ['e', 'E', '+', '.'];
const BLOCKED_DECIMAL_SYMBOLS = ['e', 'E', '+', '-'];

export const validateNumberKey = (
  e: KeyboardEvent<HTMLInputElement>,
  isDecimal: boolean
): void => {
  if (isDecimal) {
    if (BLOCKED_DECIMAL_SYMBOLS.includes(e.key)) return;

    const inputValue = (e.target as HTMLInputElement).value;

    // Prevent second decimal point
    if (e.key === '.' && inputValue.includes('.')) {
      e.preventDefault();
      return;
    }

    // Limit to two decimal places
    if (inputValue.includes('.') && inputValue.split('.')[1].length >= 2) {
      if (!isNaN(Number(e.key)) && e.key !== '.') {
        e.preventDefault();
      }
    }
  } else {
    if (BLOCKED_NUMBER_SYMBOLS.includes(e.key)) {
      e.preventDefault();
    }
  }
};

// ---- Zip code fields ------------------------------------------------------

export const validateZipKey = (
  e: KeyboardEvent<HTMLInputElement>
): void => {
  if (isModifierCombo(e)) return;

  const isNumber = /^[0-9]$/.test(e.key);
  const isDash = e.key === '-';

  if (isNumber || isDash || NAVIGATION_KEYS.includes(e.key)) {
    return;
  }
  e.preventDefault();
};

export const handleZipPaste = (e: ClipboardEvent<HTMLInputElement>): void => {
  e.preventDefault();
  const pasteContent = e.clipboardData.getData('Text');
  if (/^[0-9-]+$/.test(pasteContent)) {
    const input = e.target as HTMLInputElement;
    // Use document.execCommand for undo-friendly paste
    document.execCommand('insertText', false, pasteContent);
  }
};

// ---- Text-only fields (e.g. contact name) ---------------------------------

export const validateTextOnlyKey = (
  e: KeyboardEvent<HTMLInputElement>
): void => {
  if (isModifierCombo(e)) return;

  const isText = /^[a-zA-Z.,' -]*$/.test(e.key);
  const isDash = e.key === '-';

  if (isText || isDash || NAVIGATION_KEYS.includes(e.key)) {
    return;
  }
  e.preventDefault();
};

// ---- Date conversion helpers ----------------------------------------------

/**
 * Converts YYYY-MM-DD to MM/DD/YYYY.
 */
export const isoToSlashDate = (value: string): string => {
  const [year, month, day] = value.split('-');
  return `${month}/${day}/${year}`;
};

/**
 * Converts a slash-format date (MM/DD/YYYY or MM/DD/YY) to YYYY-MM-DD.
 */
export const slashToIsoDate = (value: string): string => {
  if (!value.includes('/')) return value;

  const [month, day, year] = value.split('/');
  const fullYear = year?.length < 4 ? `20${year}` : year;
  const paddedMonth = month?.length === 1 ? month.padStart(2, '0') : month;
  const paddedDay = day?.length === 1 ? day.padStart(2, '0') : day;

  return `${fullYear}-${paddedMonth}-${paddedDay}`;
};
